import { useState } from 'react'
import { api, type Subtask } from '../lib/api'
import { hapticFeedback, hapticNotification } from '../lib/telegram'

interface SubtasksListProps {
  taskId: string
  subtasks: Subtask[]
  onSubtaskUpdate: () => void
  showProgress?: boolean
  allowAdd?: boolean
}

export function SubtasksList({ 
  taskId, 
  subtasks, 
  onSubtaskUpdate, 
  showProgress = true,
  allowAdd = false
}: SubtasksListProps) {
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('')
  const [adding, setAdding] = useState(false)
  const [updating, setUpdating] = useState<string | null>(null)

  const completedCount = subtasks.filter(s => s.completed).length
  const totalCount = subtasks.length
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0

  const handleToggleSubtask = async (subtask: Subtask) => {
    if (updating === subtask.id) return
    
    try {
      setUpdating(subtask.id)
      hapticFeedback('light')
      
      await api.updateSubtask(subtask.id, { completed: !subtask.completed })
      
      if (!subtask.completed) {
        hapticNotification('success')
      }
      
      onSubtaskUpdate()
    } catch (e) {
      console.error('Failed to update subtask:', e)
      hapticNotification('error')
    } finally {
      setUpdating(null)
    }
  }

  const handleAddSubtask = async () => {
    if (!newSubtaskTitle.trim() || adding) return
    
    try {
      setAdding(true)
      hapticFeedback('light')
      
      await api.createSubtask(taskId, { 
        title: newSubtaskTitle.trim(),
        sort_order: subtasks.length
      })
      
      setNewSubtaskTitle('')
      onSubtaskUpdate()
    } catch (e) {
      console.error('Failed to add subtask:', e)
      hapticNotification('error')
    } finally {
      setAdding(false)
    }
  }

  const handleDeleteSubtask = async (subtaskId: string) => {
    try {
      hapticFeedback('light')
      await api.deleteSubtask(subtaskId)
      onSubtaskUpdate()
    } catch (e) {
      console.error('Failed to delete subtask:', e)
      hapticNotification('error')
    }
  }

  if (subtasks.length === 0 && !allowAdd) {
    return null
  }

  return (
    <div className="space-y-3">
      {/* Progress bar */}
      {showProgress && totalCount > 0 && (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-green-400 to-blue-500 transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="text-xs text-muted font-medium">
            {completedCount}/{totalCount}
          </span>
        </div>
      )}

      {/* Subtasks list */}
      {subtasks.length > 0 && (
        <div className="space-y-2">
          {subtasks.map((subtask) => (
            <div
              key={subtask.id}
              className={`
                flex items-center gap-3 p-2 rounded-lg transition-all
                ${subtask.completed ? 'bg-green-500/10' : 'bg-white/5'}
                ${updating === subtask.id ? 'opacity-50' : 'hover:bg-white/10'}
              `}
            >
              <button
                onClick={() => handleToggleSubtask(subtask)}
                disabled={updating === subtask.id}
                className={`
                  w-4 h-4 rounded border-2 flex items-center justify-center transition-all active:scale-90
                  ${subtask.completed 
                    ? 'bg-green-500 border-green-500' 
                    : 'border-white/30 hover:border-green-400'
                  }
                `}
              >
                {subtask.completed && (
                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
              
              <span className={`
                flex-1 text-sm transition-all
                ${subtask.completed 
                  ? 'text-white/60 line-through' 
                  : 'text-white/80'
                }
              `}>
                {subtask.title}
              </span>

              {allowAdd && (
                <button
                  onClick={() => handleDeleteSubtask(subtask.id)}
                  className="w-6 h-6 flex items-center justify-center text-white/40 hover:text-red-400 transition-colors"
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new subtask */}
      {allowAdd && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newSubtaskTitle}
            onChange={(e) => setNewSubtaskTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddSubtask()}
            placeholder="Add a subtask..."
            className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/50 focus:outline-none focus:border-accent-light/50"
            disabled={adding}
          />
          <button
            onClick={handleAddSubtask}
            disabled={!newSubtaskTitle.trim() || adding}
            className="px-3 py-2 bg-accent-light/20 text-accent-light rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent-light/30 transition-all active:scale-95"
          >
            {adding ? '...' : 'Add'}
          </button>
        </div>
      )}
    </div>
  )
}